import { decisionReasonsForKind, type VerificationDecisionReason } from '@aysar/contracts';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type ModalComponentProps } from '@/shared/components/Modals/modal-registry/modal-registry.types';
import { Button } from '@/shared/components/ui/Button/Button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/Dialog/Dialog';
import { Label } from '@/shared/components/ui/Label/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/Select/Select';
import { Textarea } from '@/shared/components/ui/Textarea/Textarea';

import { useCaseVerdictLogic } from '../../hooks/useCaseVerdictLogic/useCaseVerdictLogic';

/**
 * Refusing a member — and the one dialog in this module whose contents a
 * **member** reads.
 *
 * ## Everything here follows from that
 *
 * The reason is a **closed set**, not free text, because the member reads it in
 * their own locale: the client maps the code to translated copy, and a
 * free-text-only rejection would reach an Arabic-first member in whatever
 * language the reviewer happened to type (`VerificationDecisionSchema`'s own
 * argument). `other` exists so a reviewer is never forced into a code that does
 * not fit, and it is the one case where the note carries the whole explanation.
 *
 * The dialog therefore **shows what the member will see, word for word**, and
 * updates it as the reviewer chooses. A verdict somebody else reads should not
 * be composed blind.
 *
 * Terminal is said out loud: `rejected` has no outgoing edge in
 * `VERIFICATION_CASE_TRANSITIONS`, so this cannot be undone here — the member
 * starts a **new case** with the next attempt number, and the refused one stays
 * readable.
 *
 * Submit is disabled until a reason is picked. The contract's discriminated
 * union makes a rejection without one unrepresentable, and a button that could
 * send an incomplete verdict is a button that will.
 */
export function RejectCaseModal({ open, onCancel, modalData }: ModalComponentProps) {
  const { t } = useTranslation(['verification', 'common']);
  const [reasonCode, setReasonCode] = useState<VerificationDecisionReason | null>(null);
  const { target, note, setNote, isDeciding, error, submit } = useCaseVerdictLogic(
    'reject',
    modalData,
    onCancel,
  );

  if (target === null) {
    return null;
  }

  const member = target.member ?? t('verification:queue.missingAccount');
  const trimmedNote = note.trim();

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{t('verification:reject.title')}</DialogTitle>
          <DialogDescription>{t('verification:detail.subtitle')}</DialogDescription>
        </DialogHeader>

        <DialogBody>
          <p className="text-body text-foreground">{t('verification:reject.lead', { member })}</p>

          {/* An LTR island of its own rather than a bidi run inside the sentence
              above (P10.6j). */}
          <p dir="ltr" className="rounded-md bg-muted p-md text-body font-medium">
            {member}
          </p>

          <p className="flex items-start gap-sm text-body text-destructive">
            <AlertTriangle aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{t('verification:reject.terminal')}</span>
          </p>

          <div className="flex flex-col gap-xs">
            <Label htmlFor="verification-reject-reason">
              {t('verification:reject.reasonLabel')}
            </Label>
            <Select
              value={reasonCode ?? undefined}
              onValueChange={(next) => {
                // Radix hands back a bare string; the guard is what makes it a
                // reason, so an unknown value is ignored rather than cast.
                if (isDecisionReason(next)) setReasonCode(next);
              }}
            >
              <SelectTrigger
                id="verification-reject-reason"
                aria-describedby="verification-reject-reason-hint"
              >
                <SelectValue placeholder={t('verification:reject.reasonLabel')} />
              </SelectTrigger>
              <SelectContent>
                {/* Only what can be true of THIS case: a re-review collected no
                    evidence, so nothing about an ID photo is offered (D6). */}
                {decisionReasonsForKind(target.kind).map((reason) => (
                  <SelectItem key={reason} value={reason}>
                    {t(`verification:decision.reason.${reason}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p id="verification-reject-reason-hint" className="text-caption text-muted-foreground">
              {t('verification:reject.reasonHint')}
            </p>
          </div>

          <div className="flex flex-col gap-xs">
            <Label htmlFor="verification-reject-note">{t('verification:reject.noteLabel')}</Label>
            <Textarea
              id="verification-reject-note"
              aria-describedby="verification-reject-note-hint"
              value={note}
              maxLength={2000}
              onChange={(event) => {
                setNote(event.target.value);
              }}
            />
            <p id="verification-reject-note-hint" className="text-caption text-muted-foreground">
              {t('verification:reject.noteHint')}
            </p>
          </div>

          {/*
            What the member will read, built from the same two values that are
            about to be sent. `aria-live="polite"` because it changes as the
            reviewer chooses and the change is the point — but politely, so it
            does not interrupt somebody typing the note.
          */}
          {reasonCode === null ? null : (
            <section
              className="flex flex-col gap-xs rounded-md border border-border p-md"
              data-reject-preview
            >
              <h3 className="text-caption font-medium text-foreground">
                {t('verification:reject.preview.title')}
              </h3>
              <div aria-live="polite" className="flex flex-col gap-2xs">
                <p className="text-body text-foreground">
                  {t(`verification:decision.reason.${reasonCode}`)}
                </p>
                {trimmedNote === '' ? null : (
                  <p className="whitespace-pre-wrap break-words text-body text-muted-foreground">
                    {trimmedNote}
                  </p>
                )}
              </div>
              <p className="text-caption text-muted-foreground">
                {t('verification:reject.preview.hint')}
              </p>
            </section>
          )}

          {error === null ? null : (
            <p role="alert" className="text-caption text-destructive">
              {error}
            </p>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>
            {t('common:action.cancel')}
          </Button>
          <Button
            variant="destructive"
            isLoading={isDeciding}
            disabled={reasonCode === null}
            onClick={() => {
              if (reasonCode === null) return;
              void submit(reasonCode);
            }}
          >
            {t('verification:reject.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const REASONS: ReadonlySet<string> = new Set<string>(decisionReasonsForKind('initial'));

function isDecisionReason(value: string): value is VerificationDecisionReason {
  return REASONS.has(value);
}
